import {
  PLACEMENT_QUESTIONNAIRE_VERSION,
  PLACEMENT_QUESTIONS,
  placementAnswersSchema,
  recommendPlacement,
  type LearnerPath,
  type SubmitPlacement,
} from '@mio/shared';
import { useState } from 'react';

import { Alert } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';

import { PlacementQuestions, type PartialAnswers } from './placement-questions';
import { PlacementResult, type ModuleChoice, type PlacementModuleView } from './placement-result';
import { PlacementSummary } from './placement-summary';

import type { ReactNode } from 'react';

import '../placement.css';

interface Choice {
  start: number;
  modules: number[];
}

function applyChoice(current: Choice, module: number, choice: ModuleChoice): Choice {
  const included = new Set(current.modules);
  if (choice === 'skip') {
    included.delete(module);
    const modules = [...included].sort((a, b) => a - b);
    const first = modules[0];
    // The path keeps at least one module; skipping the last one is a no-op.
    if (first === undefined) return current;
    return { start: modules.includes(current.start) ? current.start : first, modules };
  }
  included.add(module);
  const modules = [...included].sort((a, b) => a - b);
  if (choice === 'start') return { start: module, modules };
  // "Include" on the current start demotes it: the earliest other module starts.
  const start =
    current.start === module
      ? (modules.find((number) => number !== module) ?? module)
      : current.start;
  return { start: modules.includes(start) ? start : module, modules };
}

function sameChoice(a: Choice | null, b: Choice | null) {
  if (!a || !b) return false;
  return (
    a.start === b.start &&
    a.modules.length === b.modules.length &&
    a.modules.every((number, index) => number === b.modules[index])
  );
}

/**
 * Two steps: answer, then review and adjust the recommended path. The recommendation is
 * previewed here with the same function the server uses to store it.
 */
export function PlacementForm({
  modules,
  initialAnswers,
  initialPath,
  savedAt,
  saving,
  error,
  onSave,
  secondaryAction,
  summaryActions,
  compact = false,
}: {
  modules: PlacementModuleView[];
  initialAnswers?: PartialAnswers | undefined;
  initialPath?: LearnerPath | null | undefined;
  /** When the path was last saved; shown on the summary of a saved path. */
  savedAt?: string | null | undefined;
  saving: boolean;
  error: string | null;
  onSave: (input: SubmitPlacement) => void;
  /** Rendered beside the primary action on both steps, such as "Decide later". */
  secondaryAction?: ReactNode;
  /** Rendered inside the summary card for the saved path, such as a link to open its start module. */
  summaryActions?: ((startModule: number) => ReactNode) | undefined;
  /** Single-column module list for the dialog. */
  compact?: boolean;
}) {
  const [answers, setAnswers] = useState<PartialAnswers>(initialAnswers ?? {});
  const saved: Choice | null = initialPath
    ? { start: initialPath.startModule, modules: [...initialPath.modules].sort((a, b) => a - b) }
    : null;
  const [step, setStep] = useState<'questions' | 'result'>(
    saved && placementAnswersSchema.safeParse(initialAnswers).success ? 'result' : 'questions',
  );
  const [chosen, setChosen] = useState<Choice | null>(saved);
  const complete = placementAnswersSchema.safeParse(answers);
  const answered = complete.success ? complete.data : null;
  // Cheap enough to recompute on every render; the server runs the same function on save.
  const recommendation = answered
    ? recommendPlacement(
        answered,
        modules.map((module) => ({ number: module.number, title: module.title })),
      )
    : null;
  const path =
    chosen ??
    (recommendation
      ? { start: recommendation.startModule, modules: recommendation.modules }
      : null);
  const unanswered = PLACEMENT_QUESTIONS.filter((question) => !answers[question.id]).length;

  if (step === 'questions' || !answered || !recommendation || !path) {
    return (
      <div className="space-y-6">
        <PlacementQuestions
          answers={answers}
          onChange={(id, value) => {
            setAnswers((current) => ({ ...current, [id]: value }));
            setChosen(null);
          }}
        />
        <div className="path-footer">
          <span className="path-footer-status">
            {unanswered > 0
              ? `${unanswered} question${unanswered === 1 ? '' : 's'} left`
              : 'All answered'}
          </span>
          <div className="path-footer-actions">
            {secondaryAction}
            <Button type="button" disabled={!complete.success} onClick={() => setStep('result')}>
              See my path
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const unchanged = saved !== null && sameChoice(saved, path);
  const canSave = path.modules.length > 0 && path.modules.includes(path.start) && !unchanged;
  return (
    <div className="space-y-6">
      <PlacementSummary
        modules={modules}
        recommendation={recommendation}
        startModule={path.start}
        chosenModules={path.modules}
        saved={
          saved && savedAt
            ? { updatedAt: savedAt, adjusted: initialPath?.source === 'adjusted' }
            : null
        }
        actions={unchanged && summaryActions ? summaryActions(path.start) : null}
      />
      <section className="path-section" aria-labelledby="path-adjust-title">
        <div className="path-section-heading">
          <h2 id="path-adjust-title">{saved ? 'Adjust your path' : 'Adjust before saving'}</h2>
          <p>Pick where to start and which modules to include. Nothing is locked.</p>
        </div>
        <PlacementResult
          modules={modules}
          recommendation={recommendation}
          chosenModules={path.modules}
          startModule={path.start}
          compact={compact}
          onChoose={(module, choice) => setChosen(applyChoice(path, module, choice))}
        />
      </section>
      {error ? <Alert>{error}</Alert> : null}
      <div className="path-footer">
        <span className="path-footer-status" role="status">
          {unchanged ? 'This is your saved path.' : saved ? 'Unsaved changes.' : ''}
        </span>
        <div className="path-footer-actions">
          <Button type="button" variant="ghost" onClick={() => setStep('questions')}>
            Change my answers
          </Button>
          {secondaryAction}
          <Button
            type="button"
            loading={saving}
            disabled={!canSave}
            onClick={() =>
              onSave({
                questionnaireVersion: PLACEMENT_QUESTIONNAIRE_VERSION,
                answers: answered,
                chosenStartModule: path.start,
                chosenModules: path.modules,
              })
            }
          >
            Save my path
          </Button>
        </div>
      </div>
    </div>
  );
}
